/**
 * Member Layout Component for Re:Nav-96
 *
 * Sidebar:
 *   - Chapter banner at top.
 *   - Dashboard / Photos / Groups (expandable, sub-items list every
 *     group in the chapter with localStorage cache + background
 *     refresh) / Pages (admin-authored).
 *   - Settings at the bottom.
 *   - Collapsible to an icon-only rail; preference persisted.
 *
 * Header:
 *   - Breadcrumb-style title that follows the current route:
 *     /96 → "Dashboard", /96 → Groups → "General", /96 → Tasks → "<list>"
 */

import { Outlet, NavLink, useLocation, useParams } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMyMemberships } from '@/hooks/useCan';
import {
  chaptersApi,
  foldersApi,
  pagesApi,
  groupTasksApi,
  type ChapterSummary,
  type PageSummary,
} from '@/services/api';
import type { Group } from '@/types';

// ---------------------------------------------------------------------------
// Cached groups list: localStorage + background refresh
// ---------------------------------------------------------------------------

const GROUPS_CACHE_KEY = 'navarasam96.sidebar.groupsCache';
const SIDEBAR_COLLAPSED_KEY = 'navarasam96.sidebar.collapsed';

interface CachedGroups {
  chapterId: string;
  groups: Group[];
  fetchedAt: number;
}

function readGroupsCache(chapterId: string): Group[] | null {
  try {
    const raw = window.localStorage.getItem(GROUPS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedGroups;
    if (parsed.chapterId !== chapterId) return null;
    return parsed.groups;
  } catch { return null; }
}

function writeGroupsCache(chapterId: string, groups: Group[]) {
  try {
    window.localStorage.setItem(GROUPS_CACHE_KEY, JSON.stringify({
      chapterId,
      groups,
      fetchedAt: Date.now(),
    }));
  } catch { /* ignore */ }
}

function groupsEqual(a: Group[], b: Group[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i]!, y = b[i]!;
    if (x.id !== y.id || x.name !== y.name || x.joinAction !== y.joinAction) return false;
  }
  return true;
}

function useCachedGroups(chapterId: string | undefined): Group[] {
  const [groups, setGroups] = useState<Group[]>(() => chapterId ? (readGroupsCache(chapterId) ?? []) : []);

  useEffect(() => {
    let cancelled = false;
    if (!chapterId) return;
    // Hydrate from cache first (instant).
    const cached = readGroupsCache(chapterId);
    if (cached) setGroups(cached);
    // Background refresh; only update state if different.
    foldersApi.list().then((folders) => {
      if (cancelled) return;
      const next = folders.flatMap((f) => f.groups ?? []);
      setGroups((prev) => groupsEqual(prev, next) ? prev : next);
      writeGroupsCache(chapterId, next);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [chapterId]);

  return groups;
}

// ---------------------------------------------------------------------------
// Static nav items
// ---------------------------------------------------------------------------

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
}

const DashboardIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);
const PhotosIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);
const GroupsIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);
const SettingsIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const navItems: NavItem[] = [
  { to: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
  { to: 'photos', label: 'Photos', icon: PhotosIcon },
];

const settingsItem: NavItem = { to: 'settings', label: 'Settings', icon: SettingsIcon };

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

function MemberSidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const params = useParams<{ chapterSlug?: string }>();
  const { memberships } = useMyMemberships();
  const [chapter, setChapter] = useState<ChapterSummary | null>(null);
  const [pages, setPages] = useState<PageSummary[]>([]);
  const location = useLocation();
  const onGroupsRoute = /\/m\/groups(\/|$)/.test(location.pathname);
  const [groupsOpen, setGroupsOpen] = useState<boolean>(onGroupsRoute);

  useEffect(() => {
    if (onGroupsRoute) setGroupsOpen(true);
  }, [onGroupsRoute]);

  useEffect(() => {
    let cancelled = false;
    if (!params.chapterSlug) return;
    chaptersApi.get(params.chapterSlug)
      .then((c) => { if (!cancelled) setChapter(c); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [params.chapterSlug]);

  useEffect(() => {
    let cancelled = false;
    if (!chapter) return;
    pagesApi.list('chapter', chapter.id)
      .then((rows) => { if (!cancelled) setPages(rows.filter((p) => !p.isLandingPage)); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [chapter]);

  const groups = useCachedGroups(chapter?.id);

  const isInstitutionAdmin = memberships.some(
    (m) => m.scopeType === 'institution' && m.role === 'admin',
  );
  const bannerLabel = chapter
    ? chapter.displayName
    : isInstitutionAdmin
      ? 'Admin'
      : 'Member';
  const bannerSub = chapter
    ? `/${chapter.slug}${chapter.batchYear ? ` · Class of ${chapter.batchYear}` : ''}`
    : '';

  const widthClass = collapsed ? 'w-16' : 'w-64';

  return (
    <aside className={`${widthClass} bg-secondary-dark border-r border-white/10 flex-shrink-0 flex flex-col h-screen sticky top-0 transition-all duration-200`}>
      {/* Top: chapter banner with collapse toggle */}
      <div className="p-4 border-b border-white/10 flex items-start justify-between gap-2">
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-white/40 mb-1">
              {chapter ? 'Chapter' : isInstitutionAdmin ? 'Scope' : 'Welcome'}
            </p>
            <p className="text-white font-display font-bold text-lg truncate">{bannerLabel}</p>
            {bannerSub && <p className="text-xs text-white/50 truncate">{bannerSub}</p>}
          </div>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="text-white/40 hover:text-white p-1 rounded"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {collapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            )}
          </svg>
        </button>
      </div>

      <nav className="p-2 flex-1 overflow-y-auto">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <SidebarLink key={item.to} item={item} collapsed={collapsed} />
          ))}

          {/* Groups: expandable */}
          <li>
            <div className="flex items-center">
              <NavLink
                to="groups"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg flex-1 transition-colors ${
                    isActive
                      ? 'bg-primary/20 text-primary border border-primary/30'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`
                }
                title="Groups"
              >
                {GroupsIcon}
                {!collapsed && <span className="font-medium">Groups</span>}
              </NavLink>
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => setGroupsOpen((v) => !v)}
                  className="text-white/40 hover:text-white p-1"
                  aria-label={groupsOpen ? 'Collapse groups' : 'Expand groups'}
                >
                  <svg className={`w-4 h-4 transition-transform ${groupsOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
            {!collapsed && groupsOpen && groups.length > 0 && (
              <ul className="mt-1 ml-8 space-y-0.5 border-l border-white/5 pl-2">
                {groups.map((g) => (
                  <li key={g.id}>
                    <NavLink
                      to={`groups/${g.id}`}
                      className={({ isActive }) =>
                        `block px-2 py-1.5 rounded text-sm truncate transition-colors ${
                          isActive
                            ? 'text-primary bg-primary/10'
                            : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`
                      }
                    >
                      {g.name}
                      {g.isDefault && <span className="ml-1.5 text-[9px] uppercase text-green-300/80">default</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>
        </ul>

        {/* Chapter pages */}
        {!collapsed && pages.length > 0 && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-wider text-white/40 mb-2 px-3">Pages</p>
            <ul className="space-y-1">
              {pages.map((p) => (
                <li key={p.id}>
                  <NavLink
                    to={`p/${p.slug}`}
                    className={({ isActive }) =>
                      `block px-3 py-2 rounded-lg text-sm truncate transition-colors ${
                        isActive
                          ? 'bg-primary/20 text-primary border border-primary/30'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`
                    }
                  >
                    {p.title}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      {/* Bottom: Settings + reunion date */}
      <div className="border-t border-white/10 p-2 space-y-2 bg-secondary-dark">
        <SidebarLink item={settingsItem} collapsed={collapsed} />
        {!collapsed && (
          <div className="text-center pt-1 border-t border-white/5">
            <p className="text-white/40 text-[10px] uppercase tracking-wider mt-1">Reunion Date</p>
            <p className="text-primary font-medium text-sm">June 13th, 2026</p>
          </div>
        )}
      </div>
    </aside>
  );
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <li>
      <NavLink
        to={item.to}
        title={item.label}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
            isActive
              ? 'bg-primary/20 text-primary border border-primary/30'
              : 'text-white/70 hover:text-white hover:bg-white/5'
          }`
        }
      >
        {item.icon}
        {!collapsed && <span className="font-medium">{item.label}</span>}
      </NavLink>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Mobile bottom navigation
// ---------------------------------------------------------------------------

function MobileNavBar() {
  const items: NavItem[] = [
    ...navItems,
    { to: 'groups', label: 'Groups', icon: GroupsIcon },
    settingsItem,
  ];
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-secondary-dark border-t border-white/10">
      <div className="flex justify-around py-2">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                isActive ? 'text-primary' : 'text-white/60'
              }`
            }
          >
            {item.icon}
            <span className="text-xs font-medium">{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Header — chapter slug breadcrumb + section / detail crumbs.
// ---------------------------------------------------------------------------

const SECTION_LABEL: Record<string, string> = {
  dashboard: 'Dashboard',
  photos: 'Photos',
  groups: 'Groups',
  messages: 'Groups',
  settings: 'Settings',
  tasks: 'Task list',
  p: 'Pages',
};

function MemberHeader() {
  const location = useLocation();
  const params = useParams<{ chapterSlug?: string }>();
  const chapterSlug = params.chapterSlug;
  const segments = location.pathname.split('/').filter(Boolean);

  // Resolve section + optional detail from the URL.
  // Walk forward looking for one of the SECTION_LABEL keys.
  let sectionKey = '';
  let sectionIndex = -1;
  for (let i = 0; i < segments.length; i += 1) {
    if (SECTION_LABEL[segments[i]!]) {
      sectionKey = segments[i]!;
      sectionIndex = i;
      break;
    }
  }
  const tail = sectionIndex >= 0 ? segments.slice(sectionIndex + 1) : [];
  // For /groups/:id and /tasks/:id resolve the entity name async.
  const detailId = tail[0];
  const detailKind = sectionKey === 'groups' || sectionKey === 'tasks' ? sectionKey : null;
  const detailName = useDetailName(detailKind, detailId, params.chapterSlug);

  return (
    <header className="bg-secondary-dark/50 border-b border-white/10 px-6 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0 flex items-center gap-2 flex-wrap">
        {chapterSlug && (
          <span className="text-xs uppercase tracking-wide text-white/40">/{chapterSlug}</span>
        )}
        {sectionKey && (
          <h1 className="text-xl font-display font-bold text-white">
            {SECTION_LABEL[sectionKey]}
          </h1>
        )}
        {detailName && (
          <>
            <span className="text-white/30">/</span>
            <span className="text-lg font-display font-semibold text-white truncate">{detailName}</span>
          </>
        )}
      </div>
      <div id="member-page-actions" className="flex items-center gap-2" />
    </header>
  );
}

function useDetailName(kind: string | null, id: string | undefined, chapterSlug?: string): string | null {
  const [name, setName] = useState<string | null>(null);
  const [, setRefreshKey] = useState(0); // re-render when cache changes
  // Resolve from cache synchronously (sidebar populates it).
  const cachedName = useMemo(() => {
    if (!id) return null;
    if (kind === 'groups') {
      const raw = (() => { try { return window.localStorage.getItem(GROUPS_CACHE_KEY); } catch { return null; } })();
      if (!raw) return null;
      try {
        const parsed = JSON.parse(raw) as CachedGroups;
        const found = parsed.groups.find((g) => g.id === id);
        return found?.name ?? null;
      } catch { return null; }
    }
    return null;
  }, [kind, id]);

  useEffect(() => {
    let cancelled = false;
    if (!kind || !id) { setName(null); return; }
    setName(cachedName);
    if (kind === 'groups' && !cachedName) {
      // Cache miss — wait briefly for the sidebar refresh and retry.
      const t = setTimeout(() => { if (!cancelled) setRefreshKey((k) => k + 1); }, 600);
      return () => { cancelled = true; clearTimeout(t); };
    }
    if (kind === 'tasks') {
      // Fetch task list to get its name.
      groupTasksApi.getList(id)
        .then((l) => { if (!cancelled) setName(l.name); })
        .catch(() => undefined);
    }
    return () => { cancelled = true; };
  }, [kind, id, cachedName, chapterSlug]);

  return name ?? cachedName;
}

// ---------------------------------------------------------------------------
// MemberLayout — top-level shell
// ---------------------------------------------------------------------------

export function MemberLayout() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
  });
  const toggle = useCallback(() => {
    setCollapsed((v) => {
      const next = !v;
      try { window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return (
    <div className="min-h-screen flex">
      <div className="hidden md:flex relative">
        <MemberSidebar collapsed={collapsed} onToggle={toggle} />
      </div>

      <div className="flex-1 flex flex-col pb-16 md:pb-0 min-w-0">
        <MemberHeader />
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      <MobileNavBar />
    </div>
  );
}

export default MemberLayout;
